import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ShopLogo } from './components/ShopLogo';
import LandingPage from './pages/LandingPage';
import AppLandingPage from './pages/AppLandingPage';
import WelcomeGate from './components/WelcomeGate';
import GuestBookingPage from './pages/GuestBookingPage';
import WalkInPage from './pages/WalkInPage';
import ClaimOfferPage from './pages/ClaimOfferPage';
import ConfirmAppointmentPage from './pages/ConfirmAppointmentPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import ReviewFunnelPage from './pages/ReviewFunnelPage';
import SaveContactPage from './pages/SaveContactPage';
import ManageAppointmentPage from './pages/ManageAppointmentPage';
import SeoLandingPage from './pages/SeoLandingPage';
import { SEO_LANDINGS } from './pages/seoLandings';

function App() {
  return (
    <Router>
      {/* First-touch pop-up on the website: Fresha is ending → sign up to be remembered (Jose 2026-10-09) */}
      <WelcomeGate />

      {/* Toast notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1E293B',
            color: '#fff',
            borderRadius: '12px',
          },
        }}
      />

      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingPage />} />

        {/* Guest Booking */}
        <Route path="/book" element={<GuestBookingPage />} />

        {/* Walk-In "Open Chair" — no-login QR kiosk: name+phone → next-available quarter-hour → book (Jose 2026-10-19) */}
        <Route path="/walkin" element={<WalkInPage />} />

        {/* Launch "get ready" hub — the SMS blast / QR / Google link destination (Jose 2026-10-08) */}
        <Route path="/app" element={<AppLandingPage />} />

        {/* Privacy Policy — public, for A2P 10DLC verification + SMS consent disclosure */}
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Terms & Conditions — public, referenced by the booking-form SMS opt-in (Twilio TFV 30513) */}
        <Route path="/terms" element={<TermsAndConditions />} />

        {/* Waitlist offer claim (texted link) */}
        <Route path="/claim/:token" element={<ClaimOfferPage />} />

        {/* Duplicate-appointment confirmation (texted link) — pick the real copy (Jose 2026-10-20) */}
        <Route path="/confirm/:token" element={<ConfirmAppointmentPage />} />

        {/* Review funnel (texted/emailed link after a visit). /review/demo to preview. */}
        <Route path="/review/:token" element={<ReviewFunnelPage />} />

        {/* Save SLIK Connect to contacts — "so you always know it's us, not spam" (Jose 2026-10-22, #118) */}
        <Route path="/connect" element={<SaveContactPage />} />

        {/* No-account manage (texted/emailed link) — view / cancel / reschedule without an account (Jose 2026-10-22, #136) */}
        <Route path="/manage/:token" element={<ManageAppointmentPage />} />

        {/* Login redirect (would go to customer portal) */}
        <Route
          path="/login"
          element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
                <ShopLogo className="w-20 h-20 object-contain rounded-2xl mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-3">Your SLIK Account</h1>
                <p className="text-gray-600 mb-6">
                  Sign in or create your account to <b>be remembered</b> — your cuts, rewards &amp; family, all in one place. Then save SLIK to your home screen.
                </p>
                <a
                  href="https://my.slikbarberlounge.com"
                  className="block w-full py-3 bg-slik-purple text-white rounded-full font-bold hover:opacity-90 transition-all"
                >
                  Sign in / Create account
                </a>
                <p className="text-sm text-gray-500 mt-3">
                  Been here before? Claiming your account brings your <b>history + rewards</b>.
                </p>
                <p className="text-sm text-gray-500 mt-4 pt-4 border-t border-gray-100">
                  Just want to book quickly?{' '}
                  <a href="/book" className="text-purple-600 font-medium hover:underline">
                    Book as a guest
                  </a>{' '}
                  <span className="text-gray-400">(we won’t save your info)</span>
                </p>
              </div>
            </div>
          }
        />

        {/* SEO/AEO Tier-1 keyword landing pages (Jose 2026-09-16) */}
        {SEO_LANDINGS.map((p) => (
          <Route key={p.slug} path={`/${p.slug}`} element={<SeoLandingPage page={p} />} />
        ))}

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-300">404</h1>
                <p className="text-gray-600 mt-4">Page not found</p>
                <a href="/" className="text-purple-600 font-medium hover:underline mt-2 inline-block">
                  Go home
                </a>
              </div>
            </div>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
